import sha256 from 'crypto-js/sha256'
import encHex from 'crypto-js/enc-hex'
import { STORAGE_KEYS } from '@/constants/constants'

const META_PIXEL_ID = '301002555738479'

/**
 * storeFbclidFromUrl
 *
 * Called once on app load. If `fbclid` is present in the current URL query string,
 * persists it to localStorage so downstream auth/payment calls can include it as a
 * fallback for building the `fbc` parameter on the server when the `_fbc` cookie is
 * unavailable (blocked by Safari ITP, ad blockers, or slow Pixel initialisation).
 *
 * The value is stored as-is (case-preserved) to match what Meta generated.
 */
function storeFbclidFromUrl() {
    try {
        const params = new URLSearchParams(window.location.search)
        const fbclid = params.get('fbclid')
        if (fbclid && fbclid.trim()) {
            const trimmed = fbclid.trim()
            localStorage.setItem(STORAGE_KEYS.FBCLID.key, trimmed)
            // Build fbc with the real click timestamp and persist as a permanent key.
            // This survives logout so purchases after re-login still carry valid attribution.
            // The backend's 90-day freshness check uses this timestamp — so old clicks
            // are correctly excluded rather than being relabeled as fresh.
            localStorage.setItem(STORAGE_KEYS.FBC.key, `fb.1.${Date.now()}.${trimmed}`)
        }
    } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('[metaPixel.storeFbclidFromUrl]', e)
    }
}

/**
 * getStoredFbclid
 *
 * Returns the fbclid stored by `storeFbclidFromUrl`, or the fbclid from the current
 * URL if the user just landed. Returns null if neither is available.
 */
function getStoredFbclid() {
    try {
        // Prefer fresh fbclid from URL (user just clicked the ad)
        const params = new URLSearchParams(window.location.search)
        const urlFbclid = params.get('fbclid')
        if (urlFbclid && urlFbclid.trim()) return urlFbclid.trim()

        // Fall back to persisted value from earlier in the session
        const stored = localStorage.getItem(STORAGE_KEYS.FBCLID.key)
        return stored || null
    } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('[metaPixel.getStoredFbclid]', e)
        return null
    }
}

/**
 * getStoredFbc
 *
 * Returns the best available fbc value for server-side CAPI requests.
 * Priority:
 *   1. _fbc browser cookie (set when autoConfig is enabled or by Meta directly)
 *   2. Stored fbc built at the moment of the last FB ad click (permanent localStorage key)
 *
 * Using the stored value (with accurate click timestamp) rather than rebuilding from
 * fbclid at request time gives Meta the correct click attribution window.
 */
function getStoredFbc() {
    try {
        const browserFbc = readCookie('_fbc')
        if (browserFbc) return browserFbc

        const stored = localStorage.getItem(STORAGE_KEYS.FBC.key)
        return stored || null
    } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('[metaPixel.getStoredFbc]', e)
        return null
    }
}

/**
 * encryptData function
 *
 * @param {*} data
 * @returns
 */
function encryptData(data) {
    try {
        const encrypted = sha256(data)
        return encrypted.toString(encHex)
    } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('Error encrypt the data:', e)
        return null
    }
}

/**
 * readCookie - reads a cookie value by name (used internally to avoid circular deps)
 *
 * @param {string} name
 * @returns {string|null}
 */
function readCookie(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(';').shift()
    return null
}

/**
 * buildMetaPixelUserData
 *
 * Builds the user data object for Meta Pixel Advanced Matching.
 * - em (email), ph (phone), ln (surname), zp (postcode) are SHA256-hashed
 * - fb_login_id is passed as-is (already a Facebook numeric ID)
 * - fbp and fbc are auto-read from _fbp / _fbc cookies
 * - client_user_agent is auto-read from navigator.userAgent
 * - client_ip_address must be supplied externally (not accessible in browser)
 *
 * @param {object} params
 * @param {string} [params.email]
 * @param {string} [params.phone]
 * @param {string} [params.surname]
 * @param {string|number} [params.externalId]
 * @param {string} [params.clientIpAddress]
 * @param {string|number} [params.facebookLoginId] - Facebook Login user ID (not hashed)
 * @param {string} [params.postcode] - Postal/zip code (will be SHA256-hashed)
 * @returns {object}
 */
function buildMetaPixelUserData({ email, phone, surname, externalId, clientIpAddress, facebookLoginId, postcode } = {}) {
    const userData = {}

    // Only assign hashed fields when hashing succeeds — never set em/ph/ln/zp to a
    // null/undefined value, which would corrupt Meta advanced matching for the user.
    const em = email ? encryptData(email.toLowerCase().trim()) : null
    if (em) userData.em = em
    const ph = phone ? encryptData(phone.replace(/\D/g, '')) : null
    if (ph) userData.ph = ph
    const ln = surname ? encryptData(surname.toLowerCase().trim()) : null
    if (ln) userData.ln = ln
    if (externalId) userData.external_id = String(externalId)
    if (clientIpAddress) userData.client_ip_address = clientIpAddress
    if (facebookLoginId) userData.fb_login_id = String(facebookLoginId)
    const zp = postcode ? encryptData(postcode.toLowerCase().replace(/\s/g, '')) : null
    if (zp) userData.zp = zp

    userData.client_user_agent = navigator.userAgent

    const fbp = readCookie('_fbp')
    if (fbp) userData.fbp = fbp

    const fbc = readCookie('_fbc')
    if (fbc) userData.fbc = fbc

    return userData
}

/**
 * initMetaPixelUserData
 *
 * Call once when user data becomes known (e.g. after login / profile load).
 * Also call with an empty object on logout to clear the matched user.
 *
 * @param {object} userData - result of buildMetaPixelUserData()
 */
function initMetaPixelUserData(userData = {}) {
    if (window.fbq) {
        window.fbq('init', META_PIXEL_ID, userData)
    }
}

/**
 * metaPixelTrackEvent function
 *
 * @param {string} event - Meta Pixel standard event name e.g. 'ViewContent', 'Purchase'
 * @param {object} customData - event-specific data (content_ids, value, currency, etc.)
 * @param {object|null} [userData] - optional result of buildMetaPixelUserData() for per-event
 *   advanced matching update. When provided, fbq('init') is called before the track so that
 *   additional fields (e.g. fb_login_id, zp) are included in the match for this event.
 *
 * Currency invariants enforced here:
 * - Meta requires `currency` (ISO 4217) whenever `value` is present.
 * - `value` must be a finite positive number; zero or NaN values are stripped to
 *   avoid Meta flagging the event as "same price" or "invalid currency".
 * - `currency` is normalized to a supported uppercase ISO 4217 code; unknown,
 *   lowercase, or malformed values fall back to 'IDR' so Meta never receives
 *   malformed currency data (which breaks ROAS attribution).
 * - `currency` defaults to 'IDR' if `value` is present but `currency` is omitted.
 */
const META_ALLOWED_CURRENCIES = ['IDR', 'USD', 'SGD', 'AUD', 'EUR']

function normalizeMetaCurrency(currency) {
    const code = typeof currency === 'string' ? currency.trim().toUpperCase() : ''
    return META_ALLOWED_CURRENCIES.includes(code) ? code : 'IDR'
}

function metaPixelTrackEvent(event, customData = {}, userData = null) {
    if (window.fbq) {
        const payload = { ...customData }

        // Strip value if it is not a valid positive finite number
        if ('value' in payload) {
            const val = payload.value
            if (!Number.isFinite(val) || val <= 0) {
                delete payload.value
            }
        }

        // Normalize / enforce currency whenever a valid value is present
        if ('value' in payload) {
            payload.currency = normalizeMetaCurrency(payload.currency)
        } else if ('currency' in payload) {
            // No usable value — drop the orphan currency to avoid a value-less currency param
            delete payload.currency
        }

        // Re-init with per-event user data to update advanced matching fields
        if (userData && Object.keys(userData).length > 0) {
            window.fbq('init', META_PIXEL_ID, userData)
        }

        window.fbq('track', event, payload)
    }
}

export { buildMetaPixelUserData, initMetaPixelUserData, metaPixelTrackEvent, storeFbclidFromUrl, getStoredFbclid, getStoredFbc }